import { getToken, onMessage, MessagePayload } from "firebase/messaging";
import { messaging } from "../firebase";
import { showNotification } from "@mantine/notifications";
import { getVapidKey, isVapidKeyConfigured } from "../config/vapidKeys";
import { getLob } from "./authenticationService";

// Track if we've already shown the "notifications blocked" message
let hasShownBlockedNotification = false;

/**
 * Request permission for push notifications
 * @returns Promise<NotificationPermission> - The permission status
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  // If permission was previously denied, browsers won't show the prompt again
  // User must manually enable notifications in browser settings
  if (Notification.permission === 'denied') {
    // Only show the notification once per session
    if (!hasShownBlockedNotification) {
      hasShownBlockedNotification = true;
      showNotification({
        title: 'Notifications Blocked',
        message: 'To enable notifications: Click the lock/info icon in your browser\'s address bar → Site settings → Notifications → Allow',
        color: 'orange',
        autoClose: 10000,
      });
    }
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
}

/**
 * Get the FCM registration token
 * @returns Promise<string | null> - The FCM token or null if unavailable
 */
export async function getFCMToken(): Promise<string | null> {
  if (!messaging) {
    console.warn('Firebase Cloud Messaging is not available');
    return null;
  }

  try {
    // Get the current LOB
    const lob = getLob();
    
    // Check if VAPID key is configured for this LOB
    if (!isVapidKeyConfigured(lob)) {
      console.warn(`VAPID key not configured for LOB: ${lob}. Please configure it in src/config/vapidKeys.ts`);
      return null;
    }
    
    // Get VAPID key for the current LOB
    const vapidKey = getVapidKey(lob);
    
    // Request permission first
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return null;
    }

    // Register service worker with LOB-specific scope
    const registration = await navigator.serviceWorker.register(
      `/firebase-messaging-sw.js?lob=${encodeURIComponent(lob)}`,
      { scope: '/' }
    );
    console.log('Service Worker registered:', registration);

    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;

    // Get token with LOB-specific VAPID key
    const currentToken = await getToken(messaging, {
      vapidKey: vapidKey,
      serviceWorkerRegistration: registration,
    });

    if (currentToken) {
      console.log(`FCM Token for LOB "${lob}":`, currentToken);
      return currentToken;
    } else {
      console.warn('No registration token available. Request permission to generate one.');
      return null;
    }
  } catch (error) {
    console.error('An error occurred while retrieving token:', error);
    return null;
  }
}

/**
 * Display Mantine notification from push payload
 */
function displayMantineNotification(payload: any, callback?: (payload: any) => void): void {
  const title = payload.notification?.title || payload.data?.title || '';
  const body = payload.data?.description || '';
  
  // Display notification using Mantine notifications
  if (title || body) {
    showNotification({
      title: title,
      message: body,
      color: payload.data?.color || 'blue',
      autoClose: payload.data?.autoClose ? parseInt(payload.data.autoClose) : 5000,
      onClick: () => {
        const urlToOpen = payload.data?.feature || '/dashboard';
        window.location.href = urlToOpen;
      },
    });
  }

  // Call custom callback if provided
  if (callback) {
    callback(payload);
  }
}

/**
 * Set up foreground message handler
 * This handles messages when the app is in the foreground
 * @param callback - Optional callback function to handle the message
 */
export function onForegroundMessage(
  callback?: (payload: MessagePayload) => void
): () => void {
  if (!messaging) {
    console.warn('Firebase Cloud Messaging is not available');
    return () => {};
  }

  try {
    // Listen for messages from Firebase SDK (data-only messages)
    const unsubscribe = onMessage(messaging, (payload) => {
      displayMantineNotification(payload, callback);
    });

    // Listen for messages posted from service worker (notification+data messages)
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'FOREGROUND_PUSH_NOTIFICATION') {
        displayMantineNotification(event.data.payload, callback);
      }
    };
    
    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    // Return cleanup function
    return () => {
      unsubscribe();
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  } catch (error) {
    console.error('Error setting up foreground message handler:', error);
    return () => {};
  }
}

/**
 * Initialize push notifications
 * Call this function when your app starts to set up push notifications
 * @param onTokenReceived - Callback function when token is received
 * @param onMessageReceived - Optional callback for handling foreground messages
 * @returns Promise<void>
 */
export async function initializePushNotifications(
  onTokenReceived?: (token: string) => void,
  onMessageReceived?: (payload: MessagePayload) => void
): Promise<void> {
  if (!messaging) {
    console.warn('Firebase Cloud Messaging is not available');
    return;
  }

  try {
    // Check if service workers are supported
    if ('serviceWorker' in navigator) {
      // Set up foreground message handler
      onForegroundMessage(onMessageReceived);

      // Get and handle token
      const token = await getFCMToken();
      if (token && onTokenReceived) {
        onTokenReceived(token);
      }

      console.log('Push notifications initialized successfully');
    } else {
      console.warn('Service Workers are not supported in this browser');
    }
  } catch (error) {
    console.error('Error initializing push notifications:', error);
  }
}

/**
 * Check if notifications are supported and enabled
 * @returns boolean
 */
export function isPushNotificationSupported(): boolean {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    messaging !== null
  );
}

/**
 * Get current notification permission status
 * @returns NotificationPermission
 */
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
}

